import { createLogger } from "@/logger";

const log = createLogger("telemetry");

import { DatabaseHealthStatus, DatabaseMetrics } from "@/services/DatabaseMetrics";
import { safeSetItem } from "@/utils/localStorage";
import { ResetCoordinatorConfig } from "./ResetCoordinator";

const STORAGE_KEY = "db_metrics_snapshots_v1";
const MAX_SNAPSHOTS = 100;

export type MetricsSnapshot = {
  sessionId: string;
  sessionIndex: number;
  timestamp: string;
  metrics: DatabaseMetrics;
  status: {
    inactivityDurationMs: number;
    health: DatabaseHealthStatus;
    shouldHardReset: boolean;
    shouldSoftReset: boolean;
    willHardReset: boolean;
    willSoftReset: boolean;
  };
  config: ResetCoordinatorConfig;
};

export class DatabaseMetricsStorageService {
  public saveSnapshot(snapshot: MetricsSnapshot): void {
    const existing = this.getSnapshotsInternal();
    const updated = [...existing, snapshot].slice(-MAX_SNAPSHOTS);
    this.setSnapshotsInternal(updated);
    log.debug(`[${this.constructor.name}] Saved metrics snapshot`, {
      health: snapshot.status.health,
      willHardReset: snapshot.status.willHardReset,
      willSoftReset: snapshot.status.willSoftReset,
      inactivityDurationMs: snapshot.status.inactivityDurationMs,
      snapshot,
    });
  }

  public getSnapshots(): MetricsSnapshot[] {
    return this.getSnapshotsInternal();
  }

  public clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      log.warn(`[${this.constructor.name}] Cleared ALL metrics snapshots`);
    } catch (error) {
      log.error(`[${this.constructor.name}] Error clearing snapshots`, { error });
    }
  }

  private getSnapshotsInternal(): MetricsSnapshot[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as MetricsSnapshot[];
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (error) {
      log.error(`[${this.constructor.name}] Error getting snapshots`, { error });
      return [];
    }
  }

  private setSnapshotsInternal(snapshots: MetricsSnapshot[]): void {
    safeSetItem(localStorage, STORAGE_KEY, JSON.stringify(snapshots));
  }
}

export const databaseMetricsStorageService = new DatabaseMetricsStorageService();
